/**
 * clipPreviewUrlState.ts — PLAT-10948
 *
 * Pure helpers for the shareable clip-preview URL state (`?clipId=<variantId>`).
 * The clip param carries the currently-selected aspect-ratio *variant* id, so a
 * link reproduces the exact AR the sender saw. No React/MobX/router here, so the
 * resolution logic is unit-testable directly — note the safe-zone helpers do read
 * `SAFE_ZONE_REGISTRY`, which transitively pulls in the overlay components.
 *
 * Every param this surface owns is `clip`-prefixed, which is what keeps it
 * disjoint from the transcript tab's set (`lang` / `voice` / `translationReview`
 * in `TranscriptSpeakersTab/transcriptViewUrlState.ts`). That tab is mounted for
 * the whole Content Studio session and rewrites the whole query string, so an
 * unprefixed key here is silently clobbered.
 */

import { SAFE_ZONE_REGISTRY } from '@src/safe-zones/registry';

import type { SafeZoneKey, SafeZoneSelection } from '@src/safe-zones/types';
import type { SafeZoneVariantSnapshot } from '@src/stores-v2/ClipEditSessionStore';
import type { AssociatedVideo, OtherVideo } from '@src/types.ts/story';

/**
 * Every param the clip preview owns — written by the modal, cleared together on
 * close, and stripped by any navigation that leaves the surface (a param that
 * rides along into another route reopens the preview when the user comes back).
 * The set is data, so the strip and the close path can't drift apart.
 */
export const CLIP_PREVIEW_PARAMS = [
  'clipId',
  'clipTab',
  'clipLang',
  'clipPlayback',
  'clipTranslationReview',
  'clipReviewMode',
  'clipSafeZone',
  'clipPublishRecord',
] as const;

/**
 * The Translation Review param for this surface. Exported so the bridge's seed,
 * its writer and the deep-link veil all name it once — three readers deriving
 * "is a review being restored?" independently is how the veil ended up gated on a
 * param that no longer existed (PLAT-11037).
 */
export const CLIP_REVIEW_PARAM = 'clipTranslationReview';
/** PLAT-11662 — the review's job on this surface: `translation` | `dubbing`. */
export const CLIP_REVIEW_MODE_PARAM = 'clipReviewMode';

/**
 * Whether the URL is asking for the Translation Review workspace on the clip
 * entry. Presence, not a sentinel value — the value carries the view-mode
 * granularity. The review still only renders with a selected language.
 */
export function isClipReviewRequested(params: URLSearchParams): boolean {
  return params.get(CLIP_REVIEW_PARAM) !== null;
}

/** Drop every clip-preview param, preserving all others. Returns a new object. */
export function stripClipPreviewParams(
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  CLIP_PREVIEW_PARAMS.forEach(key => next.delete(key));
  return next;
}

type PreviewLike =
  | (Pick<OtherVideo, 'id'> & {
      associatedVideos?: Pick<AssociatedVideo, 'id'>[] | null;
    })
  | null
  | undefined;

/**
 * Whether an open preview already shows the given variant — as its parent clip
 * or one of its associated AR variants. Used so a modal-driven AR switch (which
 * rewrites `?clipId`) doesn't trigger a reopen.
 */
export function clipVariantCovered(
  preview: PreviewLike,
  variantId: string,
): boolean {
  if (!preview) return false;
  return (
    preview.id === variantId ||
    (preview.associatedVideos ?? []).some(av => av.id === variantId)
  );
}

/**
 * Resolve a `?clipId=<variantId>` param to the owning clip + the aspect ratio to
 * select. The variant id may be a top-level clip (an `otherVideos` entry) or one
 * of its associated AR variants; either way the parent clip is the thing opened,
 * with the matched variant's AR selected. Returns `null` when nothing matches
 * (deleted clip, wrong story, or no permission).
 */
export function resolveClipVariant(
  otherVideos: readonly OtherVideo[] | undefined,
  variantId: string,
): { owner: OtherVideo; targetAspectRatio?: string } | null {
  if (!otherVideos) return null;
  for (const ov of otherVideos) {
    if (ov.id === variantId) {
      return { owner: ov, targetAspectRatio: ov.aspectRatio };
    }
    const variant = ov.associatedVideos?.find(av => av.id === variantId);
    if (variant) {
      return { owner: ov, targetAspectRatio: variant.aspectRatio };
    }
  }
  return null;
}

export interface ClipPreviewSyncInput {
  /** Current `?clipId` value, or null when absent. */
  clipParam: string | null;
  /**
   * The last `?clipId` value already acted on. The latch is what makes this
   * a URL-driven decision: without it, unrelated store / clip-list churn
   * re-triggers an open for a value already handled, which replaces
   * `videoClipPreview` and reloads the renderer mid-edit.
   */
  handled: string | null;
  /** The currently open preview (`videoCreator.videoClipPreview`). */
  preview: PreviewLike;
  /** The story's clip list — `undefined` while it loads. */
  otherVideos: readonly OtherVideo[] | undefined;
}

export interface ClipPreviewSync {
  action: 'open' | 'close' | 'none';
  /** The next latch value; always assign it back. */
  handled: string | null;
  /** Present only for `open` — the clip to open + the AR to select. */
  match?: { owner: OtherVideo; targetAspectRatio?: string };
}

/**
 * Decide what the URL is asking the store to do. Pure so the transition
 * table can be tested without a router or a store:
 *
 * - No `?clipId` while a preview is open **and** a param was handled earlier ⇒
 *   `close`. `handled` is the proof the param was present in a prior render, so
 *   an initial load with no param never closes, and neither does a fresh
 *   card-click open (the store is set before the modal writes the param).
 * - A value already handled ⇒ `none`, whatever the store is doing.
 * - A preview already showing that variant ⇒ `none`, but latch it: this is a
 *   modal-driven AR switch, not a request to reopen.
 * - An id that resolves against the clip list ⇒ `open`.
 * - An id that doesn't resolve ⇒ `none` **without latching**, so a deep link
 *   whose story hasn't loaded yet retries when the list arrives. A genuinely
 *   unknown id (deleted clip / wrong story / no access) just stays a no-op.
 */
export function resolveClipPreviewSync({
  clipParam,
  handled,
  preview,
  otherVideos,
}: ClipPreviewSyncInput): ClipPreviewSync {
  if (!clipParam) {
    if (preview && handled) return { action: 'close', handled: null };
    return { action: 'none', handled };
  }

  if (handled === clipParam) return { action: 'none', handled };

  if (clipVariantCovered(preview, clipParam)) {
    return { action: 'none', handled: clipParam };
  }

  const match = resolveClipVariant(otherVideos, clipParam);
  if (!match) return { action: 'none', handled };

  return { action: 'open', handled: clipParam, match };
}

/**
 * The social-overlay ("safe zone") selection, as the bare registry key —
 * `clipSafeZone=linkedin.feed_post`. `'none'` serializes to `null` so no overlay
 * leaves the URL clean.
 *
 * The per-overlay variant pills (play controls / captions) are deliberately NOT
 * encoded: every overlay defaults them all on, so a recipient lands on the
 * overlay's documented default look. A sender's pill toggles therefore don't
 * travel — a known, accepted gap rather than an oversight.
 */
export function serializeSafeZone(selection: SafeZoneSelection): string | null {
  return selection === 'none' ? null : selection;
}

/**
 * Parse a `?clipSafeZone=` value into a selection + the compound-keyed variant
 * snapshot `ClipEditSessionStore` seeds from. An unregistered overlay key yields
 * `'none'`.
 *
 * The variants map is materialised from the overlay's registry `defaultVariants`
 * rather than left empty, and that is load-bearing: the store's constructor
 * assigns the snapshot directly and never calls `selectSafeZone` (the only place
 * that seeds defaults), while `safeZoneVariant` reads `snapshot[key] ?? false`. An
 * empty map would render the overlay with every pill OFF — visibly different from
 * the sender's view and from what picking the overlay by hand produces.
 *
 * Aspect-ratio compatibility is deliberately NOT checked here — the session store
 * owns that (it is what knows the restored dimension) and applies the same
 * `safeZoneSupportsAspectRatio` rule the dropdown and the AR-change fallback use.
 */
export function parseSafeZone(value: string | null): {
  selection: SafeZoneSelection;
  variants: SafeZoneVariantSnapshot;
} {
  const none = { selection: 'none' as SafeZoneSelection, variants: {} };
  if (!value) return none;
  // A registered definition IS the validation — an unknown key yields undefined.
  const definition = SAFE_ZONE_REGISTRY[value as SafeZoneKey];
  if (!definition) return none;
  const variants: SafeZoneVariantSnapshot = {};
  for (const key of definition.variants ?? []) {
    variants[`${definition.key}.${key}`] =
      definition.defaultVariants?.[key] ?? false;
  }
  return { selection: definition.key, variants };
}

/**
 * The clip-preview params the modal writes. `null` means "absent from the URL",
 * except for `clipId` — see `computeClipPreviewSearch`.
 */
export interface ClipPreviewParams {
  /**
   * The open AR variant's Video record **id** — `Id`-suffixed like every other
   * id-valued param here (`storyId` / `videoId` / `personId` / `quoteId`), and
   * deliberately not a slug: eq-filtering a plain string field in Dato is a
   * full-model scan.
   */
  clipId: string | null;
  clipTab: string | null;
  clipLang: string | null;
  /** `live` | `rendered` — which source the preview is playing. */
  clipPlayback: string | null;
  /** `<overlay key>[,<enabled variants>]` — see `serializeSafeZone`. */
  clipSafeZone: string | null;
  /**
   * The `publish_record` id whose render Published Version is playing, when the
   * user picked one from the Downloads list. Absent = the newest publish for
   * the selected language, which is the automatic rule (PLAT-11548).
   */
  clipPublishRecord: string | null;
}

/**
 * Diff the desired clip-preview params against the current URL. Returns the next
 * params plus whether anything changed and whether this write first ADDS `?clipId`
 * (a list -> preview open, which should push a history entry; every other change
 * replaces). Only the keys in `ClipPreviewParams` are touched — everything else
 * passes through, including `clipTranslationReview` (written by the bridge).
 *
 * A null `clipId` leaves the param untouched instead of deleting it. An
 * absent `?clipId` is how `SocialClipsView` detects a Back-close, so deleting it
 * here would tear down the very modal doing the write. Some variants (e.g. an
 * unrendered format) have no persisted id, and those must keep the last value.
 */
export function computeClipPreviewSearch(
  current: URLSearchParams,
  desired: ClipPreviewParams,
): { params: URLSearchParams; changed: boolean; addingClip: boolean } {
  const params = new URLSearchParams(current);
  let changed = false;
  const apply = (key: keyof ClipPreviewParams, value: string | null) => {
    if ((params.get(key) ?? null) === value) return;
    changed = true;
    if (value == null) params.delete(key);
    else params.set(key, value);
  };
  const addingClip = !!desired.clipId && !current.get('clipId');
  if (desired.clipId != null) apply('clipId', desired.clipId);
  apply('clipTab', desired.clipTab);
  apply('clipLang', desired.clipLang);
  apply('clipPlayback', desired.clipPlayback);
  apply('clipSafeZone', desired.clipSafeZone);
  apply('clipPublishRecord', desired.clipPublishRecord);
  return { params, changed, addingClip };
}
